import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, Search, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Radio, Trophy } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { getRoster, SITE_URL, isAuthed, LOGIN_URL } from "@/lib/api";

const PAGE_SIZE = 25;
const STATE_ORDER = ["Active", "On Leave", "All"];

const stateDot = (state) => {
  const map = {
    Active: "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.85)]",
    "On Leave": "bg-amber-400",
    Suspended: "bg-rose-500",
  };
  return map[state] || "bg-slate-500";
};

const formatHours = (r) => {
  // time_total is a string like "6391:52". Fall back to mins_total.
  if (r.time_total) return r.time_total.split(":")[0] + "h";
  if (typeof r.mins_total === "number") return `${Math.round(r.mins_total / 60)}h`;
  return "—";
};

const SortHeader = ({ label, sortKey, sort, setSort, className = "" }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      onClick={() => setSort((s) => ({ key: sortKey, dir: s.key === sortKey && s.dir === "asc" ? "desc" : "asc" }))}
      data-testid={`roster-sort-${sortKey}`}
      className={`cursor-pointer select-none px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#A1A1AA] hover:text-[#FDFBF7] ${className}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label} <Icon className={`h-3 w-3 ${active ? "text-[#D4AF37]" : "opacity-60"}`} />
      </span>
    </th>
  );
};

const PilotRow = ({ p, index }) => {
  const navigate = useNavigate();
  const href = `${SITE_URL}/users/${p.id || p.pilot_id}`;
  const handleClick = () => {
    if (!isAuthed()) {
      toast.error("Please log in to view pilot profiles.");
      navigate(LOGIN_URL);
      return;
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, delay: index * 0.008 }}
      data-testid={`roster-row-${p.ident}`}
      onClick={handleClick}
      className="cursor-pointer border-b border-white/5 transition-colors duration-150 hover:bg-white/[0.03]"
    >
      <td className="px-3 py-3">
        <span className="font-mono-data text-sm font-bold text-[#D4AF37]">{p.ident}</span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${stateDot(p.state)}`} title={p.state} />
          <span className="text-sm font-semibold text-[#FDFBF7]">{p.name}</span>
          {p.atc && (
            <span className="rounded border border-white/10 bg-white/[0.03] px-1.5 py-px font-mono-data text-[10px] text-[#A1A1AA]">
              {p.atc}
            </span>
          )}
        </div>
      </td>
      <td className="hidden px-3 py-3 sm:table-cell">
        <span className="font-mono-data text-sm text-[#FDFBF7]">{p.base_icao || "—"}</span>
        {p.base_iata && <span className="ml-1.5 text-[10px] text-[#A1A1AA]">{p.base_iata}</span>}
      </td>
      <td className="hidden px-3 py-3 md:table-cell">
        <span className="rounded-full border border-[#8A1538]/30 bg-[#8A1538]/10 px-2 py-0.5 font-mono-data text-[10px] font-semibold text-[#F5B0C2]">
          {p.airline_iata || "—"}
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          {p.rank_image ? (
            <img src={p.rank_image} alt="" loading="lazy" className="h-6 w-6 rounded object-contain ring-1 ring-white/10" />
          ) : null}
          <span className="text-xs text-[#FDFBF7]">{p.rank || "—"}</span>
        </div>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="font-mono-data text-sm font-bold text-[#D4AF37]">{formatHours(p)}</span>
      </td>
      <td className="hidden px-3 py-3 text-center lg:table-cell">
        {p.awards > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-2 py-0.5 text-[11px] font-semibold text-[#D4AF37]">
            <Trophy className="h-3 w-3" /> {p.awards}
          </span>
        ) : (
          <span className="text-xs text-[#A1A1AA]">—</span>
        )}
      </td>
      <td className="hidden px-3 py-3 lg:table-cell">
        <div className="flex items-center gap-1.5">
          {p.vatsim_id && (
            <a
              href={`https://stats.vatsim.net/stats/${p.vatsim_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`VATSIM ${p.vatsim_id}`}
              className="rounded border border-white/10 bg-white/[0.03] px-1.5 py-px font-mono-data text-[10px] text-[#A1A1AA] transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
            >
              VS {p.vatsim_id}
            </a>
          )}
          {p.ivao_id && (
            <a
              href={`https://ivao.aero/members/person/details.asp?Id=${p.ivao_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`IVAO ${p.ivao_id}`}
              className="rounded border border-white/10 bg-white/[0.03] px-1.5 py-px font-mono-data text-[10px] text-[#A1A1AA] transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
            >
              IV {p.ivao_id}
            </a>
          )}
          {!p.vatsim_id && !p.ivao_id && <span className="text-xs text-[#A1A1AA]">—</span>}
        </div>
      </td>
      <td className="hidden px-3 py-3 md:table-cell">
        <span className="whitespace-nowrap text-xs text-[#A1A1AA]">{p.last_report || "—"}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <ExternalLink className="ml-auto h-3.5 w-3.5 text-[#A1A1AA] opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
      </td>
    </motion.tr>
  );
};

export default function Roster() {
  const [roster, setRoster] = useState({ data: [], total: 0, states: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("Active");
  const [hubFilter, setHubFilter] = useState("");
  const [sort, setSort] = useState({ key: "ident", dir: "asc" }); // default order: pilot ID, lowest first
  const [page, setPage] = useState(1);

  useEffect(() => {
    getRoster().then((r) => setRoster(r || { data: [], total: 0, states: {} })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const hubs = useMemo(() => {
    const set = new Set();
    roster.data.forEach((p) => p.base_icao && set.add(p.base_icao));
    return Array.from(set).sort();
  }, [roster]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = roster.data;
    if (stateFilter !== "All") out = out.filter((p) => p.state === stateFilter);
    if (hubFilter) out = out.filter((p) => p.base_icao === hubFilter);
    if (q) out = out.filter((p) =>
      (p.ident || "").toLowerCase().includes(q) ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.atc || "").toLowerCase().includes(q) ||
      (p.base_icao || "").toLowerCase().includes(q)
    );
    const cmp = (a, b) => {
      let av, bv;
      switch (sort.key) {
        case "ident": av = a.pilot_id ?? 0; bv = b.pilot_id ?? 0; break; // numeric pilot ID, so AIC10000 sorts after AIC9999
        case "name": av = a.name; bv = b.name; break;
        case "base": av = a.base_icao; bv = b.base_icao; break;
        case "awards": av = a.awards || 0; bv = b.awards || 0; break;
        case "hours":
        default: av = a.mins_total || 0; bv = b.mins_total || 0;
      }
      if (av == null) av = "";
      if (bv == null) bv = "";
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    };
    return [...out].sort(cmp);
  }, [roster, query, stateFilter, hubFilter, sort]);

  useEffect(() => {
    setPage(1);
  }, [query, stateFilter, hubFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="roster-page">
      <Navbar />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-overline flex items-center gap-2 text-[#8A1538]">
              <Users className="h-4 w-4" /> Pilot Roster
            </p>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
              Meet our{" "}
              <span className="text-[#D4AF37]" data-testid="roster-total">
                {loading ? "…" : (roster.states?.Active ?? 0)}
              </span>{" "}
              active pilots
            </h1>
            <p className="mt-3 text-sm text-[#A1A1AA] [text-wrap:pretty]">
              Every callsign on our net — from trainees earning their stripes to Training Captains with thousands of hours in the cockpit.
              Click any row to open that pilot&apos;s profile on the crew center.
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
            <span data-testid="roster-count-filtered">
              Showing <span className="font-mono-data font-bold text-[#FDFBF7]">{filtered.length.toLocaleString()}</span> of {roster.total.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by state">
            {STATE_ORDER.map((s) => {
              const active = stateFilter === s;
              const count = s === "All" ? roster.total : roster.states?.[s] ?? 0;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStateFilter(s)}
                  data-testid={`roster-filter-state-${s.toLowerCase().replace(" ", "-")}`}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                    active
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/20 hover:text-[#FDFBF7]"
                  }`}
                >
                  {s} <span className="ml-1 font-mono-data opacity-70">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={hubFilter}
              onChange={(e) => setHubFilter(e.target.value)}
              data-testid="roster-filter-hub"
              className="rounded-full border border-white/10 bg-[#12141D] px-3 py-1.5 text-xs text-[#FDFBF7] focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="">All hubs</option>
              {hubs.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#A1A1AA]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, ident, hub…"
                data-testid="roster-search"
                className="w-64 rounded-full border border-white/10 bg-[#12141D] py-1.5 pl-9 pr-3 text-xs text-[#FDFBF7] placeholder:text-[#A1A1AA]/60 focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/5 bg-[#12141D]">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="roster-table">
              <thead className="border-b border-white/5 bg-white/[0.02]">
                <tr>
                  <SortHeader label="Ident" sortKey="ident" sort={sort} setSort={setSort} />
                  <SortHeader label="Pilot" sortKey="name" sort={sort} setSort={setSort} />
                  <SortHeader label="Home" sortKey="base" sort={sort} setSort={setSort} className="hidden sm:table-cell" />
                  <th className="hidden px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#A1A1AA] md:table-cell">Airline</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Rank</th>
                  <SortHeader label="Hours" sortKey="hours" sort={sort} setSort={setSort} className="text-right" />
                  <SortHeader label="Awards" sortKey="awards" sort={sort} setSort={setSort} className="hidden text-center lg:table-cell" />
                  <th className="hidden px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#A1A1AA] lg:table-cell">
                    <span className="inline-flex items-center gap-1.5"><Radio className="h-3 w-3" /> Networks</span>
                  </th>
                  <th className="hidden px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#A1A1AA] md:table-cell">Last Report</th>
                  <th className="px-3 py-3" aria-label="Open profile" />
                </tr>
              </thead>
              <tbody className="group">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-16 text-center text-sm text-[#A1A1AA]">
                      Loading roster…
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-16 text-center text-sm text-[#A1A1AA]" data-testid="roster-empty">
                      No pilots match your filters.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((p, i) => <PilotRow key={p.id ?? p.ident} p={p} index={i} />)
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-between text-xs text-[#A1A1AA]" data-testid="roster-pagination">
            <span>
              Page <span className="font-mono-data font-bold text-[#FDFBF7]">{page}</span> of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="roster-prev"
                className="rounded-full border border-white/10 px-3 py-1.5 font-semibold text-[#FDFBF7] disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="roster-next"
                className="rounded-full border border-white/10 px-3 py-1.5 font-semibold text-[#FDFBF7] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
